import { useId, useLayoutEffect, useRef, useState } from 'react';

export type LinePoint = { key: string; label: string; value: number; hint?: string };

// One series over time (counts, not money): area + line, the nearest point under the pointer is read out.
export function LineChart({ points, height = 200, title, unit = '' }: { points: LinePoint[]; height?: number; title: string; unit?: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const grad = useId().replace(/:/g, '') + '-area';
  const box = useRef<HTMLElement>(null);
  const [W, setW] = useState(640);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => { if (e && e.contentRect.width > 0) setW(Math.round(e.contentRect.width)); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const padL = 36, padR = 8, padB = 22, padT = 10;
  const raw = Math.max(1, ...points.map((p) => p.value));
  // a round top (1, 2, 5 x 10^n) so the ticks read as whole numbers
  const mag = 10 ** Math.floor(Math.log10(raw));
  const max = [1, 2, 5, 10].map((m) => m * mag).find((m) => m >= raw) ?? raw;
  const plotW = W - padL - padR;
  const plotH = height - padB - padT;
  const x = (i: number) => padL + (points.length > 1 ? (i / (points.length - 1)) * plotW : plotW / 2);
  const y = (v: number) => padT + (1 - v / max) * plotH;
  const ticks = [0, max / 2, max].filter((v) => Number.isInteger(v));
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join('');
  const area = points.length ? `${line}L${x(points.length - 1).toFixed(1)},${y(0)}L${x(0).toFixed(1)},${y(0)}Z` : '';
  const every = Math.max(1, Math.ceil(points.length / Math.max(2, Math.floor(plotW / 90))));
  const h = hover !== null ? points[hover] : null;
  const pick = (clientX: number, svg: SVGSVGElement) => {
    if (!points.length) return;
    const r = svg.getBoundingClientRect();
    const px = ((clientX - r.left) / r.width) * W;
    const i = points.length > 1 ? Math.round(((px - padL) / plotW) * (points.length - 1)) : 0;
    setHover(Math.max(0, Math.min(points.length - 1, i)));
  };

  return (
    <figure className="chart" ref={box}>
      <div className="chart__readout" aria-live="polite">
        {h ? <><span className="chart__readout-label">{h.hint ?? h.label}</span><span className="num">{h.value.toLocaleString('vi-VN')}{unit}</span></> : <span className="muted">Rê chuột lên biểu đồ để xem số</span>}
      </div>
      <svg viewBox={`0 0 ${W} ${height}`} width={W} height={height} role="img" aria-label={title}
        onPointerMove={(e) => pick(e.clientX, e.currentTarget)} onPointerDown={(e) => pick(e.clientX, e.currentTarget)} onPointerLeave={() => setHover(null)}>
        <defs>
          <linearGradient id={grad} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" className="chart__area-top" />
            <stop offset="1" className="chart__area-bottom" />
          </linearGradient>
        </defs>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} className={t === 0 ? 'chart__zero' : 'chart__grid'} />
            <text x={padL - 8} y={y(t) + 4} className="chart__tick" textAnchor="end">{t.toLocaleString('vi-VN')}</text>
          </g>
        ))}
        {area ? <path d={area} fill={`url(#${grad})`} /> : null}
        {line ? <path d={line} className="chart__line" /> : null}
        {points.map((p, i) => (i % every === 0
          ? <text key={p.key} x={x(i)} y={height - 6} className="chart__tick" textAnchor={i === 0 ? 'start' : 'middle'}>{p.label}</text> : null))}
        {h && hover !== null ? (
          <g>
            <line x1={x(hover)} x2={x(hover)} y1={padT} y2={y(0)} className="chart__cursor" />
            <circle cx={x(hover)} cy={y(h.value)} r={4} className="chart__dot" />
          </g>
        ) : null}
      </svg>
    </figure>
  );
}
